import styled, { FlattenSimpleInterpolation } from 'styled-components'
import range from 'lodash/range'

import { breakpoints, colors, fontSizes } from '@/styles'

import { ReactComponent as Arrow } from './pagination-arrow.svg'

const LEFT_PAGE = 'LEFT'
const RIGHT_PAGE = 'RIGHT'

const Nav = styled.nav`
  display: flex;
  justify-content: center;
`

const Button = styled.button<{
  current?: boolean
  customStyle: FlattenSimpleInterpolation | undefined
}>`
  height: 30px;
  width: 30px;
  background-color: transparent;
  color: ${colors.secondary};
  font-size: ${fontSizes.regular};
  font-weight: 600;
  letter-spacing: 0;
  line-height: 22px;
  margin: 0 8px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  outline: none;
  border: none;
  appearance: none;
  position: relative;
  flex-shrink: 0;

  :disabled {
    display: none;
  }

  :first-child {
    margin-left: 0;
  }

  :last-child {
    margin-right: 0;
  }

  :hover {
    background-color: ${colors.lightGrey};
  }

  ${props =>
    props.current &&
    `
    color: ${colors.white};
    background-color: ${colors.secondary};
    border: 2px solid ${colors.secondary};
    :hover {
      background-color: ${colors.secondary};
    }
  `}

  // ACCESSIBILITY FOCUS OUTLINE
  :before {
    content: '';
    position: absolute;
    border: none;
  }
  :focus {
    :before {
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      border: 3px solid ${colors.lightBlue};
      border-radius: 29px;
    }
    :active:focus {
      :before {
        border: none;
      }
    }
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 0 2px;
  }

  ${({ customStyle }) => customStyle}
`

const LeftArrow = styled(Button)`
  transform: rotate(90deg);
  border: 2px solid ${colors.secondary};
  &:hover {
    background-color: transparent;
  }
`

const RightArrow = styled(LeftArrow)`
  transform: rotate(-90deg);
`

interface Props {
  currentPage: number
  totalRecords: number
  totalPages: number
  pageLimit: number
  pageNeighbours: number
  onPageChanged: () => void
  customStyle?: FlattenSimpleInterpolation | undefined
  preload: (p: string | number) => void
}

const Pagination: React.FC<Props> = ({
  currentPage = 1,
  totalRecords = 0,
  totalPages = 0,
  pageLimit = 10,
  pageNeighbours = 2,
  onPageChanged = (f: any) => f,
  customStyle,
  preload
}) => {
  const gotoPage = (page: string | number) => {
    const activePage = Math.max(0, Math.min(Number(page), totalPages))
    const paginationData = {
      currentPage: activePage,
      totalPages,
      pageLimit,
      totalRecords
    }
    onPageChanged(paginationData)
  }

  // const { customStyle } = props
  const handleClick = (page: string | number) => gotoPage(page)

  const handlePrevious = () => gotoPage(currentPage - 1)

  const handleNext = () => gotoPage(currentPage + 1)

  const handleMoveLeft = () => gotoPage(currentPage - pageNeighbours * 2 - 1)

  const handleMoveRight = () => gotoPage(currentPage + pageNeighbours * 2 + 1)

  /**
   * Let's say we have 10 pages and we set pageNeighbours to 2
   * Given that the current page is 6
   * The pagination control will look like the following:
   *
   * (1) < {4 5} [6] {7 8} > (10)
   *
   * (x) => terminal pages: first and last page(always visible)
   * [x] => represents current page
   * {...x} => represents page neighbours
   */
  /* eslint-disable complexity, max-statements */
  const fetchPageNumbers = () => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and
     * right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3
    const totalBlocks = totalNumbers + 2

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours)
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours)

      let pages: (string | number)[] = range(startPage, endPage + 1)
      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2
      const hasRightSpill = totalPages - endPage > 1
      const spillOffset = Math.max(0, pageNeighbours + 2 - pages.length)

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage)
          pages = [LEFT_PAGE, ...extraPages, ...pages]
          break
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset + 1)
          pages = [...pages, ...extraPages, RIGHT_PAGE]
          break
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE]
          break
        }
      }
      return [1, ...pages, totalPages]
    }
    return range(1, totalPages + 1)
  }
  /* eslint-enable */

  if (!totalRecords || totalPages === 1) return null

  const pages = fetchPageNumbers()

  return (
    <>
      <Nav aria-label="pagination">
        <LeftArrow
          onClick={handlePrevious}
          onMouseOver={() => preload(currentPage - 1)}
          disabled={currentPage === 1}
          aria-disabled={currentPage === 1}
          customStyle={customStyle}
          aria-label="Previous page"
          id="previous-page"
        >
          <Arrow height="10" width="12" />
        </LeftArrow>
        {pages.map(page => {
          switch (page) {
            case LEFT_PAGE:
              return (
                <Button
                  key={`left-page-${page}`}
                  id={`left-page-${page}`}
                  onClick={handleMoveLeft}
                  customStyle={customStyle}
                >
                  …
                </Button>
              )
            case RIGHT_PAGE:
              return (
                <Button
                  key={`right-page-${page}`}
                  id={`right-page-${page}`}
                  onClick={handleMoveRight}
                  customStyle={customStyle}
                >
                  …
                </Button>
              )
            case currentPage:
              return (
                <Button
                  key={`current-page-${page}`}
                  id={`page-${page}`}
                  onClick={() => handleClick(page)}
                  customStyle={customStyle}
                  current
                  aria-current="page"
                  aria-disabled="true"
                >
                  {page}
                </Button>
              )
            default:
              return (
                <Button
                  key={`page-${page}`}
                  id={`page-${page}`}
                  onClick={() => handleClick(page)}
                  customStyle={customStyle}
                  onMouseOver={() => {
                    if (page === currentPage) return
                    preload(page)
                  }}
                >
                  {page}
                </Button>
              )
          }
        })}
        <RightArrow
          onClick={handleNext}
          disabled={currentPage === totalPages}
          aria-disabled={currentPage === totalPages}
          onMouseOver={() => preload(currentPage + 1)}
          customStyle={customStyle}
          aria-label="Next page"
          id="next-page"
        >
          <Arrow height="10" width="12" />
        </RightArrow>
      </Nav>
    </>
  )
}

export default Pagination
